import { ChangeEvent, useEffect, useState } from "react";
import { useMapsLibrary} from '@vis.gl/react-google-maps'

import ReactPaginate from 'react-paginate';
import Locations from "./Locations";
import LocationProps from "../../props/LocationProps";
import LocationSort from "../../helpers/sort";

type PaginatedItemsProps = {
  locations: LocationProps[],
  itemsPerPage: number,
  userLatLng: google.maps.LatLngLiteral | null,
  setUserShowModal: (flag: boolean) => void
  setActiveLocation: (loc: LocationProps[]) => void
  appliedCollectables: string[]
  appliedCategories: string[]
  filtersApplied: boolean
}

const PaginatedItems = ({
  itemsPerPage,
  locations,
  userLatLng,
  setUserShowModal,
  setActiveLocation,
}:PaginatedItemsProps) => {

  const [itemOffset, setItemOffset] = useState(0)
  const [sortOrder, setSortOrder] = useState<string>('alpha')
  const [ sortedLocations, setSortedLocations ] = useState<LocationProps[]>(LocationSort.alpha(locations))
  const [ currentLocations, setCurrentLocations ] = useState<LocationProps[]>([])
  const [ currentPage, setCurrentPage ] = useState<number>(0)
  const [ itemCount, setItemCount ] = useState<number>(0)
  const [ listLocations, setListLocations ] = useState<LocationProps[]>([])
  const geo = useMapsLibrary('geometry')

  useEffect(() => {
    setCurrentLocations(() => {
      return [...sortedLocations.filter(x => x.active).slice(itemOffset, itemOffset + itemsPerPage)]
    })
  }, [sortedLocations, itemOffset, userLatLng])

  useEffect(() => {
    setItemCount(listLocations.filter(x => x.active).length)

    setSortedLocations(() => {
      const s = LocationSort[sortOrder](locations)
      return [...s]
    })
  }, [sortOrder, userLatLng, listLocations])

  const endOffset = itemOffset + itemsPerPage;
  const pageCount = Math.ceil(itemCount / itemsPerPage);

  const computeDistance = (location:LocationProps) => {
    const coords:google.maps.LatLngLiteral = location.location
    if (!userLatLng) return null
    const dist = geo?.spherical.computeDistanceBetween(new google.maps.LatLng(userLatLng.lat, userLatLng.lng), new google.maps.LatLng(coords.lat, coords.lng))
    if (!dist) return null
    const formattedDistance = (Math.round(dist) / 1000)
    location.distance = formattedDistance
    return formattedDistance
  }

  useEffect(() => {
    if (userLatLng === null && sortOrder === 'distance') {
      // if they remove their location pin we reset to alpha.
      // leave it if they've already set it to alpha or ralpha
      sortLocations('alpha')
    } else {
      locations.forEach(loc => {
        computeDistance(loc)
      })
      sortLocations('distance')
    }
  }, [userLatLng])

  // Invoke when user click to request another page.
  const handlePageClick = (selectedItem: {selected:number}) => {
    const newOffset = (selectedItem.selected * itemsPerPage) % locations.length;
    setItemOffset(newOffset);
    setCurrentPage(selectedItem.selected)
  };

  const sortFromSelect = (event:ChangeEvent) => {
    sortLocations((event.target as HTMLInputElement).value)
  }

  const sortLocations =  (val:string)=> {
    setSortOrder(val)
    setItemOffset(0)
    setCurrentPage(0)
  }

  useEffect(() => {
    if (locations?.length) {
      setListLocations(locations)
    }
  }, [locations])

  return (
    <>
      <div className="nsw-results-bar">
        <div className="nsw-results-bar__info">Showing results {Number(itemOffset + 1)} to {endOffset > itemCount ? itemCount : endOffset } of {itemCount}</div>
        <div className="nsw-results-bar__sorting">
          <label className="nsw-form__label" htmlFor="results-sort">Sort by:</label>
          <select className="nsw-form__select" id="results-sort" name="results-sort" onChange={sortFromSelect}>
            <option defaultChecked={!userLatLng} selected={sortOrder === 'alpha'} value="alpha">Name (A-Z) </option>
            <option selected={sortOrder === 'ralpha'} value="ralpha">Name (Z-A) </option>
            <option disabled={!userLatLng} selected={sortOrder === 'distance'} value="distance">Distance</option>
          </select>
        </div>
      </div>
      <Locations setUserShowModal={setUserShowModal} setActiveLocation={setActiveLocation} locations={currentLocations} userLatLng={userLatLng} sortOrder={sortOrder} />
      <div className="nsw-pagination">
        <ReactPaginate
          breakLabel="&hellip;"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          disableInitialCallback={true}
          forcePage={Math.min(currentPage, pageCount)}
          pageCount={pageCount}
          previousLabel={<><span className="material-icons nsw-material-icons" aria-hidden="true">keyboard_arrow_left</span><span className="sr-only">Previous</span></>}
          nextLabel={<><span className="material-icons nsw-material-icons" aria-hidden="true">keyboard_arrow_right</span><span className="sr-only">Next</span></>}
          renderOnZeroPageCount={null}
          containerClassName=''
          previousLinkClassName="nsw-icon-button"
          nextLinkClassName="nsw-icon-button"
          activeLinkClassName='active'
        />
      </div>
    </>
  );
}

export default PaginatedItems

